import React, { useState } from 'react';
import CaseStudyCard, { CaseStudy } from './CaseStudyCard';
import './CaseStudyCard.css';

/**
 * 客户案例展示区域组件属性
 */
interface CaseStudySectionProps {
  cases: CaseStudy[];
  className?: string;
}

/**
 * 客户案例展示区域组件
 * 一次只展示一个案例，可以通过导航切换不同案例
 * @param {CaseStudySectionProps} props - 组件属性
 * @returns {JSX.Element} 渲染的React组件
 */
const CaseStudySection: React.FC<CaseStudySectionProps> = ({ cases, className = '' }) => {
  // 当前活动案例索引
  const [activeIndex, setActiveIndex] = useState<number>(0);
  
  // 切换到指定索引的案例
  const goToCase = (index: number): void => {
    setActiveIndex(index);
  };
  
  // 切换到上一个案例
  const goToPrevious = (): void => {
    setActiveIndex((prevIndex) => (prevIndex - 1 + cases.length) % cases.length);
  };
  
  // 切换到下一个案例
  const goToNext = (): void => {
    setActiveIndex((prevIndex) => (prevIndex + 1) % cases.length);
  };
  
  return (
    <section className={`case-study-section ${className}`}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-secondary mb-4 scroll-fade-up">客户案例</h2>
          <p className="text-gray-600 max-w-2xl mx-auto scroll-fade-up scroll-delay-1">了解我们的小程序如何帮助客户解决实际问题，提升业务效率</p>
        </div>
        
        {/* 当前案例卡片 */}
        {cases.length > 0 && (
          <CaseStudyCard caseStudy={cases[activeIndex]} />
        )}
        
        {/* 案例导航 */}
        {cases.length > 1 && (
          <div className="flex justify-center items-center mt-8 gap-4">
            <button 
              onClick={goToPrevious}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="上一个案例"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7"></path>
              </svg>
            </button>
            
            <div className="flex gap-2">
              {cases.map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToCase(index)}
                  className={`w-3 h-3 rounded-full ${index === activeIndex ? 'bg-primary' : 'bg-gray-300'}`}
                  aria-label={`切换到第${index + 1}个案例`}
                />
              ))}
            </div>
            
            <button 
              onClick={goToNext}
              className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              aria-label="下一个案例"
            >
              <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default CaseStudySection;